import { FieldArray, FieldProps } from 'formik';
import {
  Input,
  Button,
  HStack,
  Text,
  VStack,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';

interface VTOArrayFieldProps extends FieldProps {
  placeholder?: string;
  maxItems?: number;
  addButtonText?: string;
  isEditing: boolean;
  showNumbers?: boolean;
  emptyStateText?: string;
}

/**
 * Formik-aware array field component for VTO
 * Uses FieldArray to manage dynamic lists of strings
 */
export function VTOArrayField({
  field,
  form,
  placeholder = '',
  maxItems,
  addButtonText = 'Add Item',
  isEditing,
  showNumbers = false,
  emptyStateText = 'Not defined',
}: VTOArrayFieldProps) {
  const values = field.value || [];

  if (!isEditing) {
    if (values.length === 0) {
      return (
        <Text color="gray.400" fontSize="sm" fontStyle="italic">
          {emptyStateText}
        </Text>
      );
    }
    return (
      <VStack spacing={1.5} align="stretch">
        {values.map((value: string, idx: number) => (
          <Text key={idx} fontSize="sm" color="gray.700">
            {showNumbers ? `${idx + 1}. ` : ''}{value}
          </Text>
        ))}
      </VStack>
    );
  }

  return (
    <FieldArray
      name={field.name}
      render={(arrayHelpers) => (
        <VStack spacing={2} align="stretch">
          {values.map((value: string, idx: number) => (
            <HStack key={idx} spacing={2}>
              {showNumbers && (
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" minW="1.5rem">
                  {idx + 1}.
                </Text>
              )}
              <Input
                value={value}
                onChange={(e) => {
                  const newValues = [...values];
                  newValues[idx] = e.target.value;
                  form.setFieldValue(field.name, newValues);
                }}
                placeholder={placeholder}
                size="sm"
              />
              <IconButton
                aria-label="Remove"
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => arrayHelpers.remove(idx)}
              />
            </HStack>
          ))}
          {(!maxItems || values.length < maxItems) && (
            <Button
              leftIcon={<AddIcon />}
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={() => arrayHelpers.push('')}
            >
              {addButtonText}
            </Button>
          )}
        </VStack>
      )}
    />
  );
}

